import * as React from 'react';
import FadeComponent from 'components/common/fadeComponent';
import { buildClassString } from '@ps/payscale-shared-ui/lib/StringUtils';
import './modal.scss';

interface ModalProps {
    className?: string;                          // class to apply to root element
    children?: React.ReactNode; // children to display in modal body
    onHide?: () => void;                         // callback to notify when modal hidden
    show: boolean;                               // whether or not to show modal
}

interface ModalState {
    visible: boolean;                            // current visible state of the modal
}

// Basic modal component which shows a dark overlay over the window with the
// modal body centered in the window
class Modal extends React.PureComponent<ModalProps, ModalState> {
    private _keyDownHandler: (e: KeyboardEvent) => void;
    private _overlay: HTMLElement | null;

    constructor(props: ModalProps) {
        super(props);
        this.state = Object.assign({}, this.state, {visible: props.show});
        this._hide = this._hide.bind(this);
        this._onOverlayClick = this._onOverlayClick.bind(this);
        this._keyDownHandler = e => this._onKeyDown(e);
    }

    componentDidMount(): void {
        document.body.addEventListener('keydown', this._keyDownHandler);
    }

    componentWillUnmount(): void {
        document.body.removeEventListener('keydown', this._keyDownHandler);
    }

    // make sure we update our state to match props for visibility
    UNSAFE_componentWillReceiveProps(nextProps: ModalProps): void {
        this.setState({
            visible: nextProps.show
        });
    }

    render(): React.ReactNode {
        const { children, className } = this.props;
        const { visible } = this.state;
        let classString = buildClassString({
            modal: true
        });
        if (className) {
            classString += ` ${className}`;
        }

        return (
            <FadeComponent show={visible} className={classString}>
                <div
                    className="modal__overlay"
                    onClick={this._onOverlayClick}
                    ref={ref => this._overlay = ref}
                >
                    <div
                        className="modal__content"
                    >
                        <button type="button" className="modal__close-button" onClick={this._hide}>
                            <i className="icon-cancel" />
                        </button>
                        {children}
                    </div>
                </div>
            </FadeComponent>
        );
    }

    // Hide the modal. Update our visibility state, and notify
    // our parent that we're no longer visible
    private _hide(): void {
        const { onHide } = this.props;
        this.setState({
            visible: false
        });

        if (onHide) {
            onHide();
        }
    }

    private _onKeyDown(e: KeyboardEvent): void {
        const { visible } = this.state;
        // on escape, close modal if visible
        if (visible && e.key === 'Escape') {
            this._hide();
        }
    }

    private _onOverlayClick(e: React.SyntheticEvent<HTMLElement>): void {
        if (e && e.target === this._overlay) {
            // only want to hide when the overlay, not any content, is clicked on
            this._hide();
        }
    }
}

export { Modal };
export default Modal;



// WEBPACK FOOTER //
// ./src/components/common/modal.tsx